import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Image as ImageIcon, MessageCircle, Loader2 } from 'lucide-react';
import { fireAchievementConfetti } from '@/utils/confetti';
import { useUserStore } from '@/store/userStore';
import { ParticleRing } from '@/components/ParticleRing';
import { Mascot } from '@/components/Mascot';
import {
  shareAchievement,
  canShareToStory,
  type ShareChannel,
  type ShareableAchievement,
} from '@/utils/achievementCard';

interface Achievement extends ShareableAchievement {
  id: string;
  category: string;
  title: string;
  description: string;
  icon: string;
  xp_reward: number;
  target_value: number;
  shareable?: boolean;
  slava_line?: string | null;
}

interface Props {
  open: boolean;
  achievement: Achievement | null;
  onClose: () => void;
  /** false — окно открыто с экрана «Достижения», чтобы поделиться позже: без конфетти. */
  celebrate?: boolean;
}

/**
 * Момент достижения.
 *
 * Было: модалка с «+100 XP» — внутренняя валюта, которая ничего не значит
 * ни внутри (уровень ничего не открывал), ни снаружи (репост непонятен).
 * Стало (решение Славы 03.09.2026): достижение — это момент, когда СЛАВА
 * ЗАМЕТИЛ, и повод унести карточку наружу. Отсюда фраза Славы вместо очков
 * и три канала в один тап вместо скриншота.
 */
export function AchievementUnlockModal({ open, achievement, onClose, celebrate = true }: Props) {
  const { profile } = useUserStore();
  const [busy, setBusy] = useState<ShareChannel | null>(null);

  useEffect(() => {
    if (open && achievement && celebrate) fireAchievementConfetti();
  }, [open, achievement?.id]);

  if (!achievement) return null;

  const name = profile?.first_name || null;
  const refCode = (profile as any)?.ref_code as string | undefined;
  // Делимся только тем, что понятно снаружи. «Записал вес впервые» никто
  // не выложит — у таких достижений остаётся только «Спасибо».
  const canShare = !!achievement.shareable && !!profile?.id;
  const isStreak = achievement.category === 'streak' && achievement.target_value > 0;
  // Крупная победа — Дофи «гордится» (корона из сфер), обычная — радуется.
  const isBigWin = achievement.target_value >= 30 || /finish|path_done|days_100/.test(achievement.id);

  async function share(channel: ShareChannel) {
    if (!profile?.id || !achievement) return;
    setBusy(channel);
    try {
      await shareAchievement(channel, achievement, { userId: profile.id, name, refCode });
    } catch (e) {
      console.error('[achievement share]', e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 overflow-y-auto"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="bg-bg-card rounded-3xl max-w-sm w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-bg-elevated flex items-center justify-center text-ink-muted hover:text-ink z-10 transition-colors"
              aria-label="Закрыть"
            >
              <X size={16} />
            </button>

            <div className="flex flex-col items-center text-center pt-2 pb-1">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
                className={isStreak ? 'mb-2' : 'mb-3'}
              >
                {/* Веха серии — это про НАКОПЛЕННОЕ, и число тут сильнее иконки:
                    человек несёт наружу «15 дней подряд», а не смайлик.
                    Остальные категории подачу не меняют. */}
                {isStreak ? (
                  <ParticleRing value={achievement.target_value} label={dayWord(achievement.target_value)} />
                ) : (
                  <div className="relative w-32 h-32">
                    <Mascot pose={isBigWin ? 'thumb' : 'celebrate'} className="w-32 h-32" />
                    <span className="absolute -bottom-1 -right-1 w-11 h-11 rounded-full bg-bg-elevated border border-line flex items-center justify-center text-2xl">
                      {achievement.icon}
                    </span>
                  </div>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="font-display text-2xl font-bold text-white mb-3"
              >
                {achievement.title}
              </motion.div>

              {/* Фраза Славы — это и есть награда. Очки ушли с глаз. */}
              {achievement.slava_line ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.45 }}
                  className="mb-1"
                >
                  <p className="text-[15px] text-ink leading-relaxed px-2">
                    «{achievement.slava_line}»
                  </p>
                  <p className="text-xs text-ink-dim mt-1.5">— Дофи</p>
                </motion.div>
              ) : (
                <p className="text-sm text-ink-muted leading-relaxed max-w-[240px]">
                  {achievement.description}
                </p>
              )}
            </div>

            {canShare ? (
              <div className="mt-5">
                <div className="text-[11px] text-ink-dim text-center mb-2">
                  Карточка с твоим результатом и ссылкой на приложение
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {canShareToStory() && (
                    <ShareButton
                      Icon={Send}
                      label="В историю"
                      busy={busy === 'story'}
                      disabled={!!busy}
                      onClick={() => share('story')}
                      accent
                    />
                  )}
                  <ShareButton
                    Icon={MessageCircle}
                    label="Другу"
                    busy={busy === 'chat'}
                    disabled={!!busy}
                    onClick={() => share('chat')}
                  />
                  <ShareButton
                    Icon={ImageIcon}
                    label="Картинкой"
                    busy={busy === 'image'}
                    disabled={!!busy}
                    onClick={() => share('image')}
                  />
                </div>
                <button
                  onClick={onClose}
                  className="w-full mt-2 py-2.5 rounded-xl bg-bg-elevated text-ink-muted text-sm active:scale-[0.98] transition-all"
                >
                  Не сейчас
                </button>
              </div>
            ) : (
              <button
                onClick={onClose}
                className="w-full mt-5 py-3 rounded-xl bg-emerald-glow text-bg font-display font-bold text-sm active:scale-[0.98] transition-all"
              >
                Спасибо
              </button>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** «15 дней», «21 день», «40 дней» — склонение под числом кольца. */
function dayWord(n: number): string {
  const ten = n % 100;
  const one = n % 10;
  if (ten >= 11 && ten <= 14) return 'дней подряд';
  if (one === 1) return 'день подряд';
  if (one >= 2 && one <= 4) return 'дня подряд';
  return 'дней подряд';
}

export function ShareButton({
  Icon, label, onClick, busy, disabled, accent,
}: {
  Icon: any; label: string; onClick: () => void; busy: boolean; disabled: boolean; accent?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`py-3 rounded-xl flex flex-col items-center gap-1.5 text-[11px] font-medium transition-all active:scale-[0.97] disabled:opacity-50 ${
        accent
          ? 'bg-emerald-glow text-bg'
          : 'bg-bg-elevated border border-line text-ink'
      }`}
    >
      {busy ? <Loader2 size={16} className="animate-spin" /> : <Icon size={16} />}
      {label}
    </button>
  );
}
